import { Form } from 'antd';
import dayjs from 'dayjs';
import { useContext, useState } from 'react';

import { useLazyGetUserActionHistoryQuery } from '@app/api/ActionsHistory';
import {
  DATE_PICKER_FORMAT,
  FRONT_WARNING,
  ISO_FORMAT_INDEX,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import type { NSmallGlobalEntities } from '@app/types';
import type { NActionsHistory } from '@app/types/ActionsHistory';

import type { Dayjs } from 'dayjs';

const today = dayjs(new Date());
const weekBeforeToday = dayjs(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));

export const useUserHistoryTab = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [actions, setActions] = useState<NActionsHistory.Action[]>([]);
  const [value, setValue] = useState<NSmallGlobalEntities.RangeValue>(null);
  const [dates, setDates] = useState<NSmallGlobalEntities.RangeValue>([
    weekBeforeToday,
    today,
  ]);

  const { activeBranchId } = useUserPrint();

  const [form] = Form.useForm();

  const { showNotification } = useContext(NotificationContext);

  const [getUserActionHistory] = useLazyGetUserActionHistoryQuery();

  type GetHistoryFn = typeof getUserActionHistory;

  const submitForm = async (
    values: {
      Date: NSmallGlobalEntities.RangeValue;
    },
    id: number | undefined,
    getHistory: GetHistoryFn
  ) => {
    if (!values.Date || !values.Date[0] || !values.Date[1])
      return showNotification('Диапазон дат не определен', FRONT_WARNING);

    const dateS = values.Date[0].format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]);
    const dateE = values.Date[1].format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]);

    if (isDifferenceGreaterThanYear(dateE, dateS)) {
      return showNotification(
        'Период не должен превышать один год',
        FRONT_WARNING
      );
    }

    if (id) {
      try {
        setIsLoading(true);

        const result = await getHistory({
          ActiveBranchId: activeBranchId,
          forUserId: id,
          dateS,
          dateE,
        }).unwrap();

        setActions(result);
      } catch (error) {
        setHistoryError(handleCatchError(error));
      } finally {
        setIsLoading(false);
      }
    }
  };

  function isDifferenceGreaterThanYear(date1: string, date2: string): boolean {
    const oneYearInMilliseconds = 365 * 24 * 60 * 60 * 1000;
    const difference = Math.abs(
      new Date(date1).getTime() - new Date(date2).getTime()
    );
    return difference > oneYearInMilliseconds;
  }

  const disabledDate = (current: Dayjs) => {
    if (!dates) {
      return false;
    }
    const tooLate = dates[0] && current.diff(dates[0], 'days') >= 365;
    const tooEarly = dates[1] && dates[1].diff(current, 'days') >= 365;
    return !!tooEarly || !!tooLate;
  };

  return {
    form,
    submitForm,
    isLoading,
    historyError,
    setHistoryError,
    actions,
    today,
    weekBeforeToday,
    disabledDate,
    value,
    setValue,
    dates,
    setDates,
    getUserActionHistory,
  };
};
